import { createSlice } from '@reduxjs/toolkit';
import {
    getProjectProductTagsByProjectProductIdAction,
    getProjectProductTagsByProjectProductIdsAction,
    createProjectProductTagAction,
    deleteProjectProductTagAction,
} from '../../actions/projectProductTagActions';
import { Tag } from '../../actions/tagActions/types';

export interface ProjectProductTag {
    id: number;
    projectProductId: number;
    tagId: number;
    tag: Tag;
}

export interface ProjectProductTagState {
    byProjectProductId: Record<
        number,
        { pending: boolean; tags: ProjectProductTag[]; fetchedAt?: number }
    >;
    pending: boolean;
}

const initialState: ProjectProductTagState = {
    byProjectProductId: {},
    pending: false,
};

export const projectProductTagSlice = createSlice({
    name: 'projectProductTags',
    initialState,
    reducers: {
        clearProjectProductTagsForProjectProduct: (state, action) => {
            delete state.byProjectProductId[action.payload];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getProjectProductTagsByProjectProductIdAction.pending, (state, action) => {
                const projectProductId = action.meta.arg;
                state.byProjectProductId[projectProductId] = { pending: true, tags: [] };
            })
            .addCase(getProjectProductTagsByProjectProductIdAction.fulfilled, (state, action) => {
                const { projectProductId, tags } = action.payload;
                state.byProjectProductId[projectProductId] = {
                    pending: false,
                    tags,
                    fetchedAt: Date.now(),
                };
            })
            .addCase(getProjectProductTagsByProjectProductIdAction.rejected, (state, action) => {
                const projectProductId = action.meta.arg;
                state.byProjectProductId[projectProductId] = { pending: false, tags: [] };
            })
            .addCase(createProjectProductTagAction.fulfilled, (state, action) => {
                const projectProductId = action.payload?.projectProductId;
                const tag = action.payload?.created;
                if (!projectProductId || !tag) {
                    return;
                }

                if (state.byProjectProductId[projectProductId]) {
                    const entry = state.byProjectProductId[projectProductId];
                    const idx = entry.tags.findIndex(
                        (t) => t.tagId === tag.tagId || t.id === tag.id,
                    );
                    if (idx === -1) {
                        entry.tags.push(tag);
                    } else {
                        entry.tags[idx] = tag;
                    }
                } else {
                    state.byProjectProductId[projectProductId] = { pending: false, tags: [tag] };
                }
            })
            .addCase(deleteProjectProductTagAction.fulfilled, (state, action) => {
                const id = action.payload?.id;
                for (const key of Object.keys(state.byProjectProductId)) {
                    const pid = Number.parseInt(key, 10);
                    const entry = state.byProjectProductId[pid];
                    if (!entry) {
                        continue;
                    }
                    entry.tags = entry.tags.filter((t) => t.id !== id);
                }
            })
            .addCase(getProjectProductTagsByProjectProductIdsAction.fulfilled, (state, action) => {
                const allTags: ProjectProductTag[] = action.payload ?? [];
                const grouped: Record<number, ProjectProductTag[]> = {};
                for (const tag of allTags) {
                    const pid = tag.projectProductId;
                    if (!grouped[pid]) {
                        grouped[pid] = [];
                    }
                    grouped[pid].push(tag);
                }
                for (const [key, tags] of Object.entries(grouped)) {
                    state.byProjectProductId[Number(key)] = {
                        pending: false,
                        tags,
                        fetchedAt: Date.now(),
                    };
                }
            });
    },
});

export const { clearProjectProductTagsForProjectProduct } = projectProductTagSlice.actions;
export default projectProductTagSlice.reducer;
